/**
 * Type system for ProposedAction API
 *
 * Mirrors Django serializers from apps/actions/serializers.py
 * and models from apps/actions/models.py
 */

// ============================================================================
// Enums and Constants
// ============================================================================

/**
 * Proposal resolution states
 */
export type ProposalResolution =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'failed'
  | 'withdrawn'
  | 'executing';

/**
 * Action type names
 */
export type ProposedActionTypeName =
  | 'timeline_event'
  | 'gmail_send'
  | 'calendar_event_create'
  | 'external_action'
  | 'onboarding_action'
  | 'create_pipeline'
  | 'ask_user_for_info'
  | 'create_automation'
  | 'create_validation_rule';

/**
 * Filter tags computed from action payloads (server-side)
 */
export type FilterTag =
  | 'email'
  | 'pipeline_update'
  | 'research'
  | 'calendar'
  | 'crm_create'
  | 'crm_delete'
  | 'crm_associate'
  | 'crm_merge'
  | 'property_update'
  | 'external_action'
  | 'onboarding'
  | 'automation';

/**
 * Action categories for UI (includes 'all' for filtering)
 */
export type ActionCategory = FilterTag | 'all';

// ============================================================================
// Action Payload Types
// ============================================================================

/**
 * Gmail send action parameters
 *
 * Email content is stored directly in action_params (not in separate Artifact records).
 */
export interface GmailSendParams {
  /** Recipient email addresses (required) */
  to: string[];
  /** Email subject (required) */
  subject: string;
  /**
   * Email body plain text. Always present as a string; may be empty for
   * reply drafts (`is_reply: true`) where the user composes the body in the
   * proposal editor. The runtime validator in ActionBuilder enforces
   * non-empty body only when `is_reply` is falsy.
   */
  body: string;
  /** CC recipients (optional) */
  cc?: string[];
  /** BCC recipients (optional) */
  bcc?: string[];
  /** HTML body (optional, takes precedence over plain text body if provided) */
  body_html?: string;
  /** Thread ID for replying to existing thread (optional) */
  thread_id?: string;
  /** File IDs to attach (optional) */
  attachment_file_ids?: string[];
  /** Whether this is a reply to an existing thread (optional) */
  is_reply?: boolean;
  /** Message ID of the email being replied to (optional) */
  reply_to_message_id?: string;
  /** Social account ID to send from (for multi-account OAuth support) */
  social_account_id?: number;
  /** Opt out of email open tracking pixel for this email */
  disable_tracking?: boolean;
  /** Editor signal for whether the draft carries an inserted signature ("inserted"),
   *  the user explicitly removed it ("user_removed"), or it was never inserted ("none").
   *  When omitted the backend defaults to "none". */
  signature_disposition?: 'none' | 'inserted' | 'user_removed';
}

/**
 * Gmail send action payload
 */
export interface GmailSendPayload {
  action_params: GmailSendParams;
  /** LLM-generated context message explaining why this action was proposed (may contain entity tags) */
  context_message?: string;
}

/**
 * Calendar event create action parameters
 */
export interface CalendarEventCreateParams {
  /** Event title (required) */
  title: string;
  /**
   * Start time in ISO 8601 format. Populated at creation by the backend
   * transform phase from the resolved slot — `string | null` exists only to
   * type legacy pre-transform payloads. Never coerce a null value into a date.
   */
  start_time: string | null;
  /** End time in ISO 8601 format. Populated at creation — see `start_time`. */
  end_time: string | null;
  /** Event description (optional) */
  description?: string;
  /** Event location (optional) */
  location?: string;
  /** Attendee email addresses (optional) */
  attendees?: string[];
  /** Calendar ID (optional, defaults to "primary") */
  calendar_id?: string;
  /** Timezone (optional, defaults to user's calendar timezone) */
  timezone?: string;
  /** Whether to attach a Google Meet video-call link (optional, defaults to true) */
  add_meet_link?: boolean;
}

/**
 * Calendar event create action payload
 */
export interface CalendarEventCreatePayload {
  action_params: CalendarEventCreateParams;
  /** LLM-generated context message explaining why this action was proposed (may contain entity tags) */
  context_message?: string;
}

/**
 * External action parameters
 *
 * For actions that direct users to complete tasks in external systems.
 */
export interface ExternalActionParams {
  /** Title of the action (required) */
  title: string;
  /** Description of what needs to be done (required) */
  description: string;
  /** URL to the external system (optional - may be null if not extractable) */
  url?: string | null;
  /** Label for the action button (required) */
  action_label: string;
  /** Source email ID if triggered by email (optional) */
  source_email_id?: string;
  /** Source email subject if triggered by email (optional) */
  source_email_subject?: string;
}

/**
 * External action payload
 */
export interface ExternalActionPayload {
  action_params: ExternalActionParams;
  /** LLM-generated context message explaining why this action was proposed (may contain entity tags) */
  context_message?: string;
}

/**
 * Team suggestion for onboarding action
 */
export interface TeamSuggestion {
  email: string;
  name: string;
  domain_match: boolean;
  total_interactions: number;
  job_title?: string;
  headline?: string;
  company_name?: string;
  is_manual?: boolean;
}

/**
 * Pipeline / stage attributes are CRM property-value dicts keyed by property
 * type name (`ahoy_name`, `ahoy_probability`, `ahoy_color`, `ahoy_description`).
 * Values are in the CRM's stored (string) form — the submit path does not
 * coerce, so `ahoy_probability` is a 0–1 fraction *string* (e.g. `"0.25"`).
 * This is the same vocabulary `crm_operation` uses everywhere.
 */
export type PipelinePropertyValues = Record<string, unknown>;

/**
 * A custom property *type* a pipeline adds to its record.
 * `options` is intentionally `unknown` because its schema varies by field_type; v1 renderers do not deep-render it.
 */
export interface PipelineTemplatePropertyType {
  property_type_name: string;
  label: string;
  field_type_name: string;
  description?: string;
  is_required?: boolean;
  is_multi?: boolean;
  options?: unknown;
}

/** A custom association *type* a pipeline adds to its record. */
export interface PipelineTemplateAssociationType {
  association_type_name: string;
  label: string;
  reverse_type_name: string;
  reverse_label: string;
  target_entity_type_name: string;
  cardinality: string;
}

/**
 * A pipeline methodology template (SPICED / BANT / MEDDIC).
 *
 * Mirrors the create_pipeline action payload field-for-field, plus the
 * template-only `key` / `description` metadata the picker uses. The wire shape
 * is served by GET /api/orgs/<org>/pipelines/pipeline_templates/.
 */
export interface PipelineTemplate {
  key: string;
  description: string;
  record_entity_type_name: string;
  pipeline_properties: PipelinePropertyValues;
  stages_properties: PipelinePropertyValues[];
  property_types: PipelineTemplatePropertyType[];
  association_types: PipelineTemplateAssociationType[];
}

/**
 * A single field specification for the ask_user_for_info action.
 * Mirrors backend FieldSpec (apps/crm_events/actions/ask_user_for_info/payload.py).
 */
export interface AskUserForInfoFieldSpec {
  key: string;
  label: string;
  description?: string;
}

/**
 * Payload of a standalone `ask_user_for_info` action.
 * Mirrors backend AskUserForInfoAction: target_entity_id (UUID → string), fields, answers.
 */
export interface AskUserForInfoPayload {
  target_entity_id: string;
  fields: AskUserForInfoFieldSpec[];
  answers: Record<string, string>;
}

/**
 * Payload of a standalone `create_automation` action.
 *
 * Read-only in v1: the proposal review UI renders these fields; approve/reject is
 * the only interaction. The payload shape is owned by the backend action package
 * (epic sibling issue) — treat these field names as the contract. The renderer
 * reads every field defensively, so a partial/malformed payload degrades to
 * em-dash / 0 rather than crashing.
 */
export interface CreateAutomationPayload {
  /** Human-readable automation name */
  display_name: string;
  /**
   * Agent-authored plain-language sentence: what the automation does (trigger →
   * action), for a non-technical reviewer. Optional on the wire for tolerance with
   * older payloads; new proposals always carry it.
   */
  summary?: string;
  /**
   * Agent-authored Markdown documentation (how it works, rules, edge cases) for
   * whoever maintains the automation. Optional on the wire; shown behind a
   * "Documentation" disclosure on the review card.
   */
  documentation?: string;
  /** CRM entity type names whose events trigger the automation */
  triggering_entity_types: string[];
  /** CRM event type names that trigger the automation */
  triggering_event_types: string[];
  /** Mapping script source (rendered read-only in a scrollable code block) */
  source: string;
  /** Sandbox-validated fixtures; only the count is surfaced in v1 */
  fixtures: unknown[];
  /** LLM-generated context message explaining why this action was proposed */
  context_message?: string;
  /** UUID, minted once when the proposal is created. Always present. */
  entity_id: string;
  /** Server-generated internal automation name (not user-facing). Always present. */
  automation_name: string;
  /** UUID of the inbound webhook entity this automation is wired to. Optional; absent or null means no webhook wiring. */
  inbound_webhook_entity_id?: string | null;
  /**
   * Recurrence for a scheduled automation. Mirrors the backend
   * `SchedulePayload` (apps/automations/actions/create_automation/payload.py).
   * Its presence is the type discriminator: the backend creates an
   * `ahoy_scheduled_automation` when set and an `ahoy_event_automation`
   * otherwise, so the frontend must branch on the same field.
   */
  schedule?: AutomationSchedulePayload | null;
  /**
   * Agent-supplied config values, validated at proposal time against the
   * schema the script's `class Config` declaration implies. Written to the
   * record as `ahoy_config` on approval. Absent/null when the script declares
   * no config.
   */
  config?: Record<string, unknown> | null;
  /**
   * The config JSON Schema derived server-side from the script's
   * `class Config` declaration and stamped onto the payload at proposal
   * creation (`prepare_new_payloads` in
   * apps/automations/actions/create_automation/handler.py) — a display cache
   * so review surfaces can decompose `config` before approval, exactly as the
   * saved record's `ahoy_config_schema` does after. Absent/null when the
   * script declares no config, and on proposals stored before the stamp
   * existed.
   */
  config_schema?: Record<string, unknown> | null;
}

/** The `schedule` of a scheduled automation: an RFC 5545 rrule anchored at `dtstart` in `tz`. */
export interface AutomationSchedulePayload {
  rrule: string;
  dtstart: string;
  tz: string;
}

/**
 * Payload of a standalone `create_validation_rule` action.
 *
 * Mirrors the backend's stored payload field names (not the LLM-input
 * names) — see apps/crm_events/actions/create_validation_rule/payload.py.
 * `enabled` arrives as a JSON boolean (only the CRM *property value* is a
 * "true"/"false" string).
 */
export interface CreateValidationRulePayload {
  display_name: string;
  target_entity_type_name: string;
  condition: string;
  constraint: string;
  message: string;
  enabled: boolean;
  /** UUID, minted once when the proposal is created. Always present. */
  entity_id: string;
}

/**
 * Payload of a standalone `create_pipeline` action.
 *
 * The backend serializes the Pydantic `CreatePipelineAction`: a flat,
 * record-anchored shape whose pipeline/stage attributes are property-value
 * dicts (the same shape a `PipelineTemplate` carries, minus the `key` /
 * `description` template metadata).
 */
export interface CreatePipelinePayload {
  record_entity_type_name: string;
  pipeline_properties: PipelinePropertyValues;
  stages_properties: PipelinePropertyValues[];
  property_types: PipelineTemplatePropertyType[];
  association_types: PipelineTemplateAssociationType[];
  context_message?: string;
}

/**
 * Onboarding action parameters
 *
 * For actions that suggest team members to invite during onboarding.
 */
export interface OnboardingActionParams {
  /** Title of the action (required) */
  title: string;
  /** Description of what needs to be done (required) */
  description: string;
  /** Suggested team members to invite (optional - not used for customize_pipeline) */
  suggestions?: TeamSuggestion[];
  /** Selected email addresses (optional, defaults to all suggestions) */
  selected_emails?: string[];
  /** Action variant key (optional) */
  action_key?:
    | 'invite_team'
    | 'meet_your_team'
    | 'customize_pipeline'
    | 'review_permissions';
  /** Pipeline stages (optional - only for customize_pipeline) */
  stages?: Array<{ name: string; color?: string }>;
  /** Existing team members for "meet your team" variant (optional) */
  existing_members?: Array<{
    id: string;
    name: string;
    email: string;
    avatar_url?: string;
    is_inviter: boolean;
  }>;
  /** ID of user who invited this user (optional) */
  invited_by_user_id?: string | null;
  /** Whether user can invite team members (optional) */
  can_invite?: boolean;
  /** Organization name (optional) */
  org_name?: string;
  /** Whether email visibility is enabled (optional - only for review_permissions) */
  email_visibility_enabled?: boolean;
  /** Whether calendar visibility is enabled (optional - only for review_permissions) */
  calendar_visibility_enabled?: boolean;
  /** UUID of user's Gmail integration timeline (optional - only for review_permissions) */
  gmail_timeline_id?: string;
  /** UUID of user's Calendar integration timeline (optional - only for review_permissions) */
  gcal_timeline_id?: string;
  /** UUID of user's Outlook Mail integration timeline (optional - only for review_permissions) */
  outlook_mail_timeline_id?: string;
  /** UUID of user's Outlook Calendar integration timeline (optional - only for review_permissions) */
  outlook_calendar_timeline_id?: string;
}

/**
 * Onboarding action payload
 */
export interface OnboardingActionPayload {
  action_params: OnboardingActionParams;
}

/**
 * Timeline event action payload
 */
export interface TimelineEventPayload {
  /** Entity ID (UUID) */
  entity_id: string;
  /** Entity type (e.g., "ahoy_contact") */
  entity_type_name: string;
  /** Event type (e.g., "ahoy_property_updated") */
  event_type_name: string;
  /** Event payload data */
  payload: Record<string, unknown>;
  /** LLM-generated context message explaining why this action was proposed (may contain entity tags) */
  context_message?: string;
}

/**
 * Property value wrapper with optional metadata
 */
export interface PropertyValueWrapper {
  value: unknown;
}

/**
 * Property update event payload (inner payload structure)
 */
export interface PropertyUpdateEventPayload {
  /** New property values to set */
  properties: Record<string, PropertyValueWrapper>;
  /** Original values at proposal creation time (for conflict detection).
   *  May be absent when the AI creates proposals without snapshotting originals. */
  original_values?: Record<string, PropertyValueWrapper>;
}

/**
 * Full timeline event payload for property updates
 */
export interface PropertyUpdateEntityPayload extends Omit<
  TimelineEventPayload,
  'payload'
> {
  event_type_name: 'ahoy_property_updated';
  payload: PropertyUpdateEventPayload;
}

// ============================================================================
// CRM Operation Payload Types
// ============================================================================

/**
 * Object created event payload (inner payload structure)
 */
export interface ObjectCreatedEventPayload {
  /** Properties for the new object */
  properties: Record<string, unknown>;
}

/**
 * Full timeline event payload for object creation
 */
export interface ObjectCreatedEntityPayload extends Omit<
  TimelineEventPayload,
  'payload' | 'event_type_name'
> {
  event_type_name: 'ahoy_object_created';
  payload: ObjectCreatedEventPayload;
}

/**
 * Object deleted event payload (inner payload structure)
 * Empty object - deletion has no additional data
 */
export type ObjectDeletedEventPayload = Record<string, never>;

/**
 * Full timeline event payload for object deletion
 */
export interface ObjectDeletedEntityPayload extends Omit<
  TimelineEventPayload,
  'payload' | 'event_type_name'
> {
  event_type_name: 'ahoy_object_deleted';
  payload: ObjectDeletedEventPayload;
}

/**
 * Association event payload (inner payload structure)
 */
export interface AssociationEventPayload {
  /** Type of association (e.g., "ahoy_contact_to_company") */
  association_type_name: string;
  /** Target entity ID to associate with */
  target_entity_id: string;
  /**
   * Snapshot of the source entity's prior target of this association type, captured
   * at proposal creation. Tri-state:
   *   - string  → the record's prior target (the "current" stage before the change)
   *   - null    → the record had NO prior target of this type at creation
   *   - absent  → legacy payload created before this snapshot existed; consumers fall
   *               back to the live CRM store read.
   */
  original_target_entity_id?: string | null;
}

/**
 * Full timeline event payload for association added
 */
export interface AssociationAddedEntityPayload extends Omit<
  TimelineEventPayload,
  'payload' | 'event_type_name'
> {
  event_type_name: 'ahoy_association_added';
  payload: AssociationEventPayload;
}

/**
 * Full timeline event payload for association removed
 */
export interface AssociationRemovedEntityPayload extends Omit<
  TimelineEventPayload,
  'payload' | 'event_type_name'
> {
  event_type_name: 'ahoy_association_removed';
  payload: AssociationEventPayload;
}

/**
 * Object merged event payload (inner payload structure)
 *
 * Backend contract (apps/crm_events/api/events.py, produced by
 * timeline_event_tool_create_handler.py): the top-level `entity_id` is the
 * surviving / canonical entity that is kept, and the inner `source_entity_id`
 * is the duplicate entity that is deleted after the merge.
 */
export interface ObjectMergedEventPayload {
  /** Source entity ID — the duplicate that is deleted after merging in */
  source_entity_id: string;
}

/**
 * Full timeline event payload for object merge
 */
export interface ObjectMergedEntityPayload extends Omit<
  TimelineEventPayload,
  'payload' | 'event_type_name'
> {
  event_type_name: 'ahoy_object_merged';
  payload: ObjectMergedEventPayload;
}

/**
 * Enriched data for CRM actions (provided by backend for display)
 */
export interface EnrichedActionData {
  /** Display label for the object */
  object_label: string;
  /** Entity type name (e.g., "ahoy_contact", "ahoy_company") */
  entity_type_name: string;
  /** Whether the primary entity already exists in the CRM */
  exists_in_crm?: boolean;
  /** Whether the entity is hidden (auto-synced but not user-acknowledged) */
  is_hidden?: boolean;
  /** Current property values (for context) */
  current_values?: Record<string, unknown>;
  /** Target object info (for associations and merges) */
  target_object?: {
    entity_id: string;
    label: string;
    entity_type_name: string | null;
    exists_in_crm?: boolean;
  };
  /** Source object info (for merges) */
  source_object?: {
    entity_id: string;
    label: string;
    entity_type_name: string | null;
    exists_in_crm?: boolean;
  };
  /** Meeting-slot ids referenced in this action's body, pre-resolved to times (keyed by slot id) */
  resolved_slots?: Record<
    string,
    { start: string; end: string; label: string }
  >;
}

/**
 * Discriminated union of all action payload types
 */
export type ActionPayload =
  | GmailSendPayload
  | CalendarEventCreatePayload
  | ExternalActionPayload
  | TimelineEventPayload
  | PropertyUpdateEntityPayload
  | ObjectCreatedEntityPayload
  | ObjectDeletedEntityPayload
  | AssociationAddedEntityPayload
  | AssociationRemovedEntityPayload
  | ObjectMergedEntityPayload
  | OnboardingActionPayload
  | CreatePipelinePayload
  | AskUserForInfoPayload
  | CreateAutomationPayload
  | CreateValidationRulePayload;

// ============================================================================
// Artifact Types (for versioning)
// ============================================================================

/**
 * Artifact type metadata
 */
export interface ArtifactType {
  name: string;
  description: string;
}

/**
 * Email artifact details (from EmailArtifactSerializer)
 */
export interface EmailArtifactDetails {
  /** When email was sent (null if not sent) */
  sent_at: string | null;
  /** Delivery status */
  delivery_status: string | null;
  /** To addresses */
  to_addresses: string[];
  /** CC addresses */
  cc_addresses: string[];
  /** BCC addresses */
  bcc_addresses: string[];
  /** Reply-to message ID */
  reply_to_message_id: string | null;
  /** Reply-to thread ID */
  reply_to_thread_id: string | null;
  /** Whether this is a reply */
  is_reply: boolean;
}

/**
 * Artifact (from ArtifactSerializer)
 *
 * Used for artifact versioning - returned by /reasoning/artifacts/ endpoints
 */
export interface Artifact {
  /** Artifact ID */
  id: number;
  /** Artifact title */
  title: string;
  /** Artifact content (structure varies by type) */
  content: unknown;
  /** Artifact status */
  status: string;
  /** Artifact type metadata */
  artifact_type: ArtifactType;
  /** Conversation ID (null if team-scoped) */
  conversation: number | null;
  /** When artifact was created */
  created_at: string;
  /** When artifact was last updated */
  updated_at: string;
  /** Version number (1-indexed) */
  version_number: number;
  /** Version notes (e.g., "Auto-versioned: Manual edit") */
  version_notes: string | null;
  /** Whether this is the current version */
  is_current_version: boolean;
  /** Parent artifact ID (null for v1) */
  parent_artifact: number | null;
  /** Whether this version was created by AI */
  created_by_agent: boolean;
  /** Email-specific details (only for email artifacts) */
  email_details: EmailArtifactDetails | null;
  /** Number of versions (annotated) */
  version_count: number;
  /** Whether artifact has versions */
  has_versions: boolean;
  /** Whether artifact requires review */
  requires_review: boolean;
  /** Review configuration */
  review_config: unknown | null;
  /** Whether artifact has been executed */
  executed: boolean;
  /** When artifact was executed */
  executed_at: string | null;
  /** Execution result */
  execution_result: unknown | null;
  /** Associated action ID (for proposal artifacts) */
  associated_action_id: number | null;
  /** Associated action data */
  associated_action: unknown | null;
}

// ============================================================================
// Execution Result Types
// ============================================================================

/**
 * Verification target the user should consult to resolve an
 * `indeterminate` action. Drives the action-type-specific copy and link
 * shown by the resolve-indeterminate affordance.
 */
export type VerificationTarget =
  | 'gmail_sent_folder'
  | 'google_calendar'
  | 'onboarding_settings'
  | 'manual_verification';

/**
 * Execution result for a single action
 *
 * Status semantics:
 * - `success`        — action committed cleanly
 * - `error` / `failure` — action did not commit; retry per `is_retryable`
 * - `partial_success` — some sub-operations committed (rare)
 * - `in_flight`      — transient marker written BEFORE a side effect; should
 *                      never persist long-term once a retry runs
 * - `indeterminate`  — worker crashed mid-side-effect; side effect MAY have
 *                      committed. User must verify externally (see
 *                      `verification_target`) and resolve via the
 *                      resolve-indeterminate endpoint.
 * - `skipped`        — action was not run in the attempt (deselected by the
 *                      user, or its dependency was deselected); written by
 *                      ExecuteActionService with a `reason`. A neutral
 *                      not-run state, not an error.
 */
export interface ExecutionResult {
  /** Execution status */
  status:
    | 'success'
    | 'error'
    | 'partial_success'
    | 'failure'
    | 'in_flight'
    | 'indeterminate'
    | 'skipped';
  /** Message ID (for gmail_send) */
  message_id?: string;
  /** Thread ID (for gmail_send) */
  thread_id?: string;
  /** Error message if status is 'error' or 'indeterminate' */
  error?: string;
  /** Where the user should verify the action's outcome (for indeterminate). */
  verification_target?: VerificationTarget;
  /** Whether the user manually resolved this action via the resolve-indeterminate endpoint. */
  resolved_by_user?: boolean;
  /** ISO timestamp of manual resolution (if resolved_by_user). */
  resolved_at?: string;
  /** Original status before manual resolution (for audit). */
  original_status?: string;
  /** Original error_type before manual resolution (for audit). */
  original_error_type?: string;
  /** Original verification_target before manual resolution (for audit). */
  original_verification_target?: VerificationTarget;
  /** Number of invitations sent (for onboarding_action) */
  invited_count?: number;
  /** Number of failed invitations (for onboarding_action) */
  error_count?: number;
  /** Successfully created invitations (for onboarding_action). UUIDs serialize to strings. */
  created?: Array<{ email: string; invitation_id: string }>;
  /** Pre-existing invitations (for onboarding_action). UUIDs serialize to strings. */
  existing?: Array<{ email: string; invitation_id: string }>;
  /** Failed invitations (for onboarding_action) */
  errors?: Array<{ email: string; error: string }>;
  /** CRM event error type (for EventValidationError) */
  error_type?: string;
  /** Whether the error is retryable (false for validation errors, true for transient failures) */
  is_retryable?: boolean;
  /** Per-event validation errors with context (for EventValidationError) */
  validation_errors?: Array<{
    event_index: number;
    entity_type: string;
    event_type: string;
    entity_id?: string;
    idempotency_key?: string;
    message: string;
  }>;
  /** External id (gmail message id or google calendar event id) of the prior send that triggered DuplicateSendBlocked. May be null when the prior reservation was still in-flight. */
  duplicate_external_id?: string | null;
  /** ISO-8601 timestamp of when the prior reservation was created — for DuplicateSendBlocked results */
  duplicate_reserved_at?: string | null;
  /** Additional metadata */
  [key: string]: unknown;
}

/**
 * Overall proposal execution result
 */
export interface ProposalExecutionResult {
  /** Total number of actions attempted */
  total_actions: number;
  /** Number of successful actions */
  successful: number;
  /** Number of failed actions */
  failed: number;
  /** Number of skipped actions (optional) */
  skipped?: number;
  /** Number of actions already executed prior to this run (not re-run) */
  already_executed?: number;
  /** Detailed results per action (optional) */
  action_results?: ExecutionResult[];
}

/**
 * Proposal-level execution error shape, sent by `apps/actions/tasks/__init__.py`
 * when execution fails outright (as opposed to a partial-failure success shape).
 */
export interface ProposalExecutionErrorResult {
  /** Human-readable error message */
  error: string;
  /** Error classification (e.g. "OrphanedExecution") */
  error_type: string;
  /** Number of actions that completed before the failure (optional) */
  actions_completed?: number;
  /** Number of actions that failed before the overall failure (optional) */
  actions_failed?: number;
  /** Total number of actions in the proposal (optional) */
  total_actions?: number;
}

/**
 * Overall execution_result shape written when the SYSTEM withdraws a
 * proposal — see apps/actions/services/transforms.py:167
 * (build_withdraw_put). Distinct from the success/error result shapes.
 */
export interface ProposalWithdrawalResult {
  reason: string | null;
  withdrawn_by: string;
}

/**
 * Empty-object shape for a proposal that has never been executed. The backend
 * field is a `JSONField(default=dict)` (see apps/actions/models.py:86), so it
 * sends `{}`, not `null`, until the proposal is actually executed.
 */
export type ProposalNotExecutedResult = Record<string, never>;

/**
 * Lightweight execution status for polling
 */
export interface ProposalExecutionStatus {
  id: number;
  resolution: ProposalResolution;
  execution_result:
    | {
        total_actions: number;
        successful: number;
        failed: number;
        skipped?: number;
      }
    | {
        error: string;
        error_type: string;
      }
    | null;
  resolved_at: string | null;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Server-computed verdict for a single action, from ActionVerdictService
 * (batched against live CRM state). Distinct from the client-side
 * `ActionVerdict` in `lib/proposals/utils/actionValidity.ts`, which derives a
 * verdict locally from already-loaded data.
 */
export interface ServerActionVerdict {
  status:
    | 'applicable'
    | 'no_op'
    | 'duplicate_of'
    | 'missing_entity'
    | 'not_visible';
  duplicate_of_action_id: number | null;
  missing_entity_ids: string[];
  labels: Record<string, string>;
}

/**
 * Proposed action (from ProposedActionSerializer)
 */
export interface ProposedAction<T extends ActionPayload = ActionPayload> {
  /** Action ID */
  id: number;
  /** Execution order (0-indexed) */
  execution_order: number;
  /** Action type */
  proposed_action_type_name: ProposedActionTypeName;
  /** Decrypted payload */
  payload: T;
  /** When action was executed (null if not executed) */
  executed_at: string | null;
  /** Execution result (null if not executed) */
  execution_result: ExecutionResult | null;
  /** Version number (1-indexed, only present for versioned actions) */
  version_number?: number;
  /** Whether this is the current/latest version */
  is_current_version?: boolean;
  /** Optional notes about this version */
  version_notes?: string | null;
  /** When this version was created */
  created_at?: string;
  /** When this version was last updated */
  updated_at?: string;
  /** Parent action ID (for versions) */
  parent_action?: number | null;
  /** Whether this version was created by AI */
  created_by_agent?: boolean;
  /** Email of user who last updated this version (null for AI-created or initial versions) */
  updated_by_email?: string | null;
  /** Enriched data for timeline_event actions (null for other types) */
  enriched_data?: EnrichedActionData | null;
  /** Server-computed verdict against live CRM state; null off the detail path. */
  verdict?: ServerActionVerdict | null;
  /** Whether this action is selected for execution (defaults to true) */
  is_selected?: boolean;
  /** IDs of actions this action depends on (action cannot execute if dependencies are deselected) */
  depends_on_action_ids?: number[];
  /**
   * Resolved entity references from the proposal's `source_context.entity_ids`,
   * paired with each entity's type. Ordered by entity `created_at` desc.
   *
   * The backend does not encode rendering knowledge — it returns *every*
   * resolvable entity in the org. The frontend filters by its own renderable
   * types (e.g. `EMBEDDED_ENTITY_RENDERERS` in `ExternalActionCard`) and picks
   * the first match. Picking the first match against a desc-ordered list
   * preserves the prior "freshest renderable entity" UX.
   *
   * Shape:
   *   - `null` for non-external_action actions or on backend resolution error
   *   - `[]` when source_context has no resolvable entity_ids
   *   - non-empty list otherwise
   */
  entity_refs?: Array<{
    entity_id: string;
    entity_type_name: string;
  }> | null;
  /**
   * Embedded version history for this logical action, sorted by version_number
   * ascending. Includes the current version. Present when the proposal was
   * fetched with `?include=versions`; absent otherwise.
   *
   * Eliminates the per-action `/versions/` round-trips that previously
   * happened in Wave 3 of the proposal page load.
   */
  versions?: ProposedAction<T>[];
  /** Server-computed reason the action's submit is blocked (issue #11040); null/absent = not blocked. */
  submit_blocked_reason?: string | null;
}

/**
 * Proposal list item (from AhoyProposalListSerializer)
 *
 * Optimized for list views - no action payloads, uses annotated action_count
 */
export interface ProposalListItem {
  /** Proposal ID */
  id: number;
  /** Proposal title */
  title: string;
  /** Proposal description */
  description: string;
  /** AI-generated reasoning summary (null if not provided) */
  reasoning_summary: string | null;
  /** Current resolution state */
  resolution: ProposalResolution;
  /** ID of currently assigned user (null if unassigned) */
  assigned_user: number | null;
  /** ID of originally assigned user */
  originally_assigned_user: number | null;
  /** ID of user who resolved (approved/rejected) */
  resolved_by: number | null;
  /** When proposal was resolved */
  resolved_at: string | null;
  /** When the assigned user first read this proposal via the detail endpoint. Null if unread. */
  read_at: string | null;
  /** Computed filter tags (email, pipeline_update, etc.) */
  filter_tags: FilterTag[];
  /** Source type (e.g., "ahoy_user_agent_conversation") */
  source_type_name: string;
  /** Source ID (UUID or other identifier) */
  source_id: string;
  /** When proposal was created */
  created_at: string;
  /** When proposal was last updated */
  updated_at: string;
  /** Number of actions (annotated by backend for performance) */
  action_count: number;
  /** Primary recipient email for avatar display (null for non-email actions) */
  primary_recipient_email: string | null;
  /** Associated CRM contact (looked up by primary_recipient_email) */
  associated_contact: {
    id: string;
    entity_id: string;
    name: string;
    email: string;
    avatar_url: string | null;
  } | null;
  /** Associated CRM company (via contact association) */
  associated_company: {
    id: string;
    entity_id: string;
    name: string;
    domain: string;
  } | null;
  /** Root entity IDs this proposal is scoped to (companies or contacts) */
  root_entity_ids: string[];
  /** Source context (channel, account, direction, counterparty, preview) */
  source_context?: SourceContext | null;
  /** Proposal-level research citations (evidence behind the proposal). Distinct from source_context.
   *  Empty for non-research proposals. */
  sources?: Array<{
    url: string;
    title: string;
    snippet?: string | null;
    published_at?: string | null;
  }>;
  /** Priority tier (1 = highest, 4 = lowest). Backend-computed. Defaults to 4 if not provided. */
  priority?: number;
}

/**
 * UI definition for custom proposal rendering
 * Matches the ViewDefinition type from dynamic views
 */
export interface ProposalUIDefinition {
  version: string;
  layout?: {
    type: string;
    props: Record<string, unknown>;
  };
  components: ProposalUIComponent[];
}

/**
 * Component in a proposal UI definition
 */
export interface ProposalUIComponent {
  id: string;
  type: string;
  props: Record<string, unknown>;
  children?: ProposalUIComponent[];
}

export interface ClarifyingQuestionOption {
  id: string;
  label: string;
  description?: string;
}

export interface ClarifyingQuestion {
  id: string;
  header: string;
  prompt: string;
  options: ClarifyingQuestionOption[];
  multiSelect: boolean;
  allowOther: boolean;
  gatesActions?: boolean;
}

export interface ClarifyingAnswer {
  questionId: string;
  kind: 'option' | 'other' | 'skipped';
  optionIds?: string[];
  text?: string;
  reason?: string;
}

export interface ClarifyingQuestions {
  ad_version: number | null;
  question_set_id: string | null;
  questions: ClarifyingQuestion[];
  answers: ClarifyingAnswer[];
  /**
   * ISO-8601 timestamp stamped by the backend when the answers were submitted
   * to the agent; cleared by any subsequent non-submit (autosave) update, so a
   * present value durably means "these exact answers were sent". Absent on
   * envelopes created before the field existed.
   */
  answers_submitted_at?: string | null;
}

/**
 * Proposal detail (from AhoyProposalDetailSerializer)
 *
 * Includes full decrypted action payloads - more expensive to retrieve
 */
export interface ProposalDetail extends Omit<ProposalListItem, 'action_count'> {
  /** Organization ID */
  org: number;
  /**
   * Overall execution result. The backend's `JSONField(default=dict)` (see
   * apps/actions/models.py:86) sends `{}` — not `null` — for any
   * not-yet-executed proposal, including a freshly reopened one.
   */
  execution_result:
    | ProposalExecutionResult
    | ProposalExecutionErrorResult
    | ProposalWithdrawalResult
    | ProposalNotExecutedResult
    | null;
  /** All actions with decrypted payloads */
  actions: ProposedAction[];
  /** Discussion conversation ID (null if no discussion yet) */
  discussion_conversation: number | null;
  /** Custom UI definition for rendering (null if using default action-based UI) */
  ui_definition: ProposalUIDefinition | null;
  /** Clarifying questions and answers (null if no clarifying questions) */
  clarifying_questions: ClarifyingQuestions | null;
  /** ISO time of the most recent failed execution that reverted this proposal to pending; null otherwise. */
  last_execution_failed_at: string | null;
}

// ============================================================================
// API Request Types
// ============================================================================

/**
 * Action payload pair for proposal approval
 *
 * Pairs an action ID with its modified payload. The payload must match the
 * exact API structure that will be used for hash comparison to detect stale data.
 */
export interface ActionPayloadPair {
  /** Action ID */
  id: number;
  /** Action payload matching API structure for hash verification */
  payload: Record<string, unknown>;
}

/**
 * Action input for proposal creation (from ProposalActionInputSerializer)
 */
export interface ProposalActionInput<T extends ActionPayload = ActionPayload> {
  /** Execution order (0-indexed) */
  execution_order: number;
  /** Action type */
  proposed_action_type_name: ProposedActionTypeName;
  /** Action payload (will be encrypted by backend) */
  payload: T;
}

/**
 * Create proposal request (from CreateProposalSerializer)
 */
export interface CreateProposalRequest {
  /** Proposal title (max 255 chars) */
  title: string;
  /** Proposal description (optional) */
  description?: string;
  /** ID of user to assign to */
  assigned_user_id: number;
  /** Source type name (max 100 chars) */
  source_type_name: string;
  /** Source ID (max 255 chars) */
  source_id: string;
  /** Actions to include */
  actions: ProposalActionInput[];
  /** Source context (channel, account_identifier, etc.) */
  source_context?: SourceContext;
}

/**
 * Update action payload request (from UpdatePayloadSerializer)
 */
export interface UpdatePayloadRequest {
  /** New payload (will be re-encrypted by backend) */
  payload: ActionPayload;
}

/**
 * Reject categories accepted by the actions reject endpoint.
 * Mirrors RejectProposalSerializer.category choices in
 * apps/actions/serializers/__init__.py, EXCLUDING 'wrong_entity' and
 * 'wrong_value': the backend requires a `correction_data` object for those
 * two (see RejectProposalSerializer.validate), and RejectProposalRequest has
 * no such field. No proposals-module caller sends either category today; if
 * one needs to, add `correction_data` to RejectProposalRequest and restore
 * the categories here (no wrong_tone — that belongs to the separate
 * reasoning ChainFeedback serializer, not this endpoint).
 */
export type ProposalRejectCategory =
  | 'hallucination'
  | 'incomplete'
  | 'unhelpful'
  | 'cannot_answer'
  | 'other';

/**
 * Reject proposal request (from RejectProposalSerializer)
 */
export interface RejectProposalRequest {
  /** Rejection reason (optional) */
  reason?: string;
  /** Structured rejection category (optional). */
  category?: ProposalRejectCategory;
}

/**
 * Reassign proposal request (from ReassignProposalSerializer)
 */
export interface ReassignProposalRequest {
  /** ID of user to reassign to */
  new_assignee_user_id: number;
}

/**
 * Update clarifying answers request
 */
export interface UpdateClarifyingAnswersRequest {
  answers: ClarifyingAnswer[];
  /** True marks the answer set as submitted to the agent (stamps
   * answers_submitted_at server-side); omitted/false on autosave, which
   * clears the stamp. */
  submitted?: boolean;
}

// ============================================================================
// API List Response Types
// ============================================================================

/**
 * Paginated list response (Django REST Framework pagination)
 */
export interface PaginatedResponse<T> {
  /** Total count of items */
  count: number;
  /** URL to next page (null if last page) */
  next: string | null;
  /** URL to previous page (null if first page) */
  previous: string | null;
  /** Items for current page */
  results: T[];
}

/**
 * Filter tag counts returned by backend
 */
export type FilterTagCounts = Partial<Record<FilterTag, number>>;

/**
 * Paginated proposals list response with filter tag counts
 */
export interface ProposalsListResponse extends PaginatedResponse<ProposalListItem> {
  /** Counts per filter tag (computed from all proposals, not just current page) */
  filter_tag_counts: FilterTagCounts;
}

// ============================================================================
// Query Filter Types
// ============================================================================

/**
 * Filters for proposal list queries
 */
export interface ProposalListFilters {
  /** Filter by resolution state */
  resolution?: ProposalResolution;
  /** Filter to only proposals assigned to current user */
  assigned_to_me?: boolean;
  /** Filter by tag */
  filter_tags__contains?: FilterTag;
  /** Filter by source type */
  source_type_name?: string;
  /** Page number for pagination (1-indexed) */
  page?: number;
  /** Number of items per page */
  page_size?: number;
  /**
   * Include expensive recipient data (primary_recipient_email, associated_contact, associated_company).
   * When true, the backend performs decryption and CRM lookups which can be slow for large lists.
   * Default is false for performance - only enable for small lists that need this data.
   */
  include_recipient_data?: boolean;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard for gmail send payload
 * Checks for gmail-specific fields (to, subject) to distinguish from calendar events
 */
export function isGmailSendPayload(
  payload: ActionPayload
): payload is GmailSendPayload {
  if (!('action_params' in payload)) {
    return false;
  }
  const params = (payload as GmailSendPayload).action_params;
  return 'to' in params && 'subject' in params;
}

/**
 * Type guard for calendar event create payload
 * Checks for calendar-specific fields (title, start_time, end_time)
 */
export function isCalendarEventCreatePayload(
  payload: ActionPayload
): payload is CalendarEventCreatePayload {
  if (!('action_params' in payload)) {
    return false;
  }
  const params = (payload as CalendarEventCreatePayload).action_params;
  return 'title' in params && 'start_time' in params && 'end_time' in params;
}

/**
 * Type guard for external action payload
 * Checks for external_action-specific fields (title, description, action_label) to distinguish from other action types
 * Note: url is optional and may be null/undefined
 */
export function isExternalActionPayload(
  payload: ActionPayload
): payload is ExternalActionPayload {
  if (!('action_params' in payload)) {
    return false;
  }
  const params = (payload as ExternalActionPayload).action_params;
  // Check for required fields: title, description, action_label
  // url is optional - don't require it
  return (
    'title' in params &&
    'description' in params &&
    'action_label' in params &&
    !('start_time' in params) &&
    !('to' in params)
  );
}

/**
 * Type guard for onboarding action payload
 * Checks for onboarding-specific fields (suggestions or action_key) which are unique to onboarding actions
 */
export function isOnboardingActionPayload(
  payload: ActionPayload
): payload is OnboardingActionPayload {
  if (!('action_params' in payload)) {
    return false;
  }
  const params = (payload as OnboardingActionPayload).action_params;
  // Check for suggestions field (team invites) or action_key (customize_pipeline, etc.)
  return (
    ('suggestions' in params && Array.isArray(params.suggestions)) ||
    ('action_key' in params && params.action_key !== undefined)
  );
}

/**
 * Type guard that narrows a payload to a standalone `create_pipeline` action —
 * the flat, record-anchored shape (record_entity_type_name + property-dict
 * pipeline/stage attributes + typed property/association definitions).
 */
export function isCreatePipelinePayload(
  payload: unknown
): payload is CreatePipelinePayload {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const p = payload as Partial<CreatePipelinePayload>;
  return (
    typeof p.record_entity_type_name === 'string' &&
    !!p.pipeline_properties &&
    typeof p.pipeline_properties === 'object' &&
    Array.isArray(p.stages_properties) &&
    Array.isArray(p.property_types) &&
    Array.isArray(p.association_types)
  );
}

/**
 * Type guard for ask_user_for_info payload.
 * Unique discriminator: top-level fields + answers + target_entity_id (no other payload carries all three).
 */
export function isAskUserForInfoPayload(
  payload: unknown
): payload is AskUserForInfoPayload {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const p = payload as Partial<AskUserForInfoPayload>;
  return (
    typeof p.target_entity_id === 'string' &&
    Array.isArray(p.fields) &&
    !!p.answers &&
    typeof p.answers === 'object'
  );
}

/**
 * Type guard for the standalone `create_automation` payload.
 *
 * Lenient by design: requires only that the payload is an object carrying a
 * string `display_name`. Individual fields (source, fixtures, trigger lists) are
 * read defensively by the renderer so a partial payload degrades gracefully.
 */
export function isCreateAutomationPayload(
  payload: unknown
): payload is CreateAutomationPayload {
  return (
    !!payload &&
    typeof payload === 'object' &&
    'display_name' in payload &&
    typeof (payload as CreateAutomationPayload).display_name === 'string'
  );
}

/**
 * Type guard for create_validation_rule payload.
 * Unique discriminator: top-level condition + constraint + target_entity_type_name
 * (no other payload carries all three).
 */
export function isCreateValidationRulePayload(
  payload: unknown
): payload is CreateValidationRulePayload {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const p = payload as Partial<CreateValidationRulePayload>;
  return (
    typeof p.display_name === 'string' &&
    typeof p.target_entity_type_name === 'string' &&
    typeof p.condition === 'string' &&
    typeof p.constraint === 'string' &&
    typeof p.enabled === 'boolean'
  );
}

/**
 * Type guard for timeline event payload
 */
export function isTimelineEventPayload(
  payload: ActionPayload
): payload is TimelineEventPayload {
  return (
    'entity_id' in payload &&
    'entity_type_name' in payload &&
    'event_type_name' in payload
  );
}

/**
 * Type guard for property update entity payload
 *
 * Checks for 'properties' in the inner payload. original_values is optional —
 * AI-generated proposals may not include original value snapshots.
 */
export function isPropertyUpdatePayload(
  payload: ActionPayload
): payload is PropertyUpdateEntityPayload {
  return (
    isTimelineEventPayload(payload) &&
    payload.event_type_name === 'ahoy_property_updated' &&
    payload.payload !== null &&
    typeof payload.payload === 'object' &&
    'properties' in payload.payload
  );
}

/**
 * Type guard for ProposalDetail
 */
export function isProposalDetail(
  proposal: ProposalListItem | ProposalDetail
): proposal is ProposalDetail {
  return 'actions' in proposal;
}

// ============================================================================
// CRM Operation Type Guards
// ============================================================================

/**
 * Type guard for object created payload
 */
export function isObjectCreatedPayload(
  payload: ActionPayload
): payload is ObjectCreatedEntityPayload {
  return (
    isTimelineEventPayload(payload) &&
    payload.event_type_name === 'ahoy_object_created' &&
    payload.payload !== null &&
    typeof payload.payload === 'object' &&
    'properties' in payload.payload
  );
}

/**
 * Type guard for object deleted payload
 */
export function isObjectDeletedPayload(
  payload: ActionPayload
): payload is ObjectDeletedEntityPayload {
  return (
    isTimelineEventPayload(payload) &&
    payload.event_type_name === 'ahoy_object_deleted'
  );
}

/**
 * Type guard for association added payload
 */
export function isAssociationAddedPayload(
  payload: ActionPayload
): payload is AssociationAddedEntityPayload {
  return (
    isTimelineEventPayload(payload) &&
    payload.event_type_name === 'ahoy_association_added' &&
    payload.payload !== null &&
    typeof payload.payload === 'object' &&
    'association_type_name' in payload.payload &&
    'target_entity_id' in payload.payload
  );
}

/**
 * Type guard for association removed payload
 */
export function isAssociationRemovedPayload(
  payload: ActionPayload
): payload is AssociationRemovedEntityPayload {
  return (
    isTimelineEventPayload(payload) &&
    payload.event_type_name === 'ahoy_association_removed' &&
    payload.payload !== null &&
    typeof payload.payload === 'object' &&
    'association_type_name' in payload.payload &&
    'target_entity_id' in payload.payload
  );
}

/**
 * Type guard for any association payload (added or removed)
 */
export function isAssociationPayload(
  payload: ActionPayload
): payload is AssociationAddedEntityPayload | AssociationRemovedEntityPayload {
  return (
    isAssociationAddedPayload(payload) || isAssociationRemovedPayload(payload)
  );
}

/**
 * Type guard for object merged payload
 */
export function isObjectMergedPayload(
  payload: ActionPayload
): payload is ObjectMergedEntityPayload {
  return (
    isTimelineEventPayload(payload) &&
    payload.event_type_name === 'ahoy_object_merged' &&
    payload.payload !== null &&
    typeof payload.payload === 'object' &&
    'source_entity_id' in payload.payload
  );
}

/**
 * Check if an action payload is a destructive CRM operation (delete or merge)
 */
export function isDestructiveCrmPayload(payload: ActionPayload): boolean {
  return isObjectDeletedPayload(payload) || isObjectMergedPayload(payload);
}

/**
 * Check if an action payload is any CRM operation type
 */
export function isCrmOperationPayload(payload: ActionPayload): boolean {
  return (
    isObjectCreatedPayload(payload) ||
    isObjectDeletedPayload(payload) ||
    isAssociationPayload(payload) ||
    isObjectMergedPayload(payload)
  );
}

// ============================================================================
// Source Context Types
// ============================================================================

/**
 * Context about where a proposal originated from
 * Provides channel-agnostic source information for display
 */
export interface SourceContext {
  /** Channel type (gmail, whatsapp, linkedin, slack, ahoy_chat, ahoy_agent, ahoy_research, crm_event) */
  channel: string;
  /** Account or connection identifier (email, phone, handle) */
  account_identifier: string;
  /** Internal account/connection ID (optional) */
  account_id?: string;
  /** Direction of communication (optional) */
  direction?: 'received' | 'sent' | 'mention' | 'inbound' | string;
  /** Counterparty name or identifier (optional) */
  counterparty?: string;
  /** Preview text from the source (optional) */
  preview?: string;
  /** CRM entity ids linked to this proposal for provenance (optional) */
  entity_ids?: string[];
  /** CRM timeline event ids linked to this proposal for provenance (optional) */
  event_ids?: number[];
  /** Channel-specific extras */
  [key: string]: unknown;
}

// ============================================================================
// Trigger and Thread Types (for email context)
// ============================================================================

/**
 * Email provider served by the ?include=trigger / ?include=thread endpoints:
 * the MailboxAPI `provider` name (`gmail`, `outlook_mail`, and the synthetic
 * dev/QA `test_integration` mailbox), plus the legacy `ms365` trigger enum.
 * Map to a web UI with `emailProviderFamily`.
 */
export type EmailProvider =
  | 'gmail'
  | 'ms365'
  | 'outlook_mail'
  | 'test_integration';

/**
 * Trigger email data returned by ?include=trigger API
 * Shows the email that triggered a reply action
 */
export interface TriggerEmailData {
  trigger_type: 'email';
  provider: EmailProvider;
  email: {
    /** Sender email/name */
    from: string;
    /** Recipient email addresses */
    to: string[];
    /** CC email addresses */
    cc: string[];
    /** Email subject line */
    subject: string;
    /** ISO date string */
    date: string;
    /** Plain text body */
    body: string;
    /** HTML body (optional) */
    body_html?: string;
    /** Gmail message ID */
    message_id: string;
    /** Short preview of email body */
    snippet: string;
  };
  /** URL to open email in external client */
  external_link: string;
}

/**
 * Individual email message in a thread
 */
export interface ThreadEmailMessage {
  /** Message ID */
  id: string;
  /** Sender email/name */
  from: string;
  /** Recipient email addresses */
  to: string[];
  /** CC email addresses */
  cc: string[];
  /** Email subject line */
  subject: string;
  /** ISO date string */
  date: string;
  /** Plain text body */
  body: string;
  /** HTML body (optional) */
  body_html?: string;
  /** Short preview of email body */
  snippet: string;
  /** Whether this message is the trigger (being replied to) */
  is_trigger: boolean;
}

/**
 * Full thread data returned by ?include=thread API
 * Shows the complete email conversation
 */
export interface ThreadData {
  trigger_type: 'email';
  provider: EmailProvider;
  /** Gmail thread ID */
  thread_id: string;
  /** Total number of messages in thread */
  message_count: number;
  /** All messages in thread, chronologically ordered */
  messages: ThreadEmailMessage[];
  /** URL to open thread in external client */
  external_link: string;
}

/**
 * Associated calendar event data for meeting notes
 */
export interface AssociatedMeeting {
  /** Meeting title */
  title: string;
  /** Meeting start time (ISO string) */
  start_time: string | null;
  /** Meeting end time (ISO string) */
  end_time: string | null;
  /** List of attendee names */
  attendees: string[];
  /** Meeting timeline ID (for linking to calendar event) */
  timeline_id?: string | null;
  /** Meeting timeline type name */
  timeline_type_name?: string | null;
}

/**
 * CRM event trigger data returned by ?include=trigger API
 * Shows the CRM event that triggered a proposal (e.g., meeting notes)
 */
export interface CrmEventTriggerData {
  trigger_type: 'crm_event';
  crm_event: {
    /** Entity type (e.g., "ahoy_meeting_notes_entity") */
    entity_type_name: string;
    /** Entity UUID */
    entity_id: string;
    /** Human-readable display name (e.g., meeting title) */
    display_name: string;
    /** Merged event payload data (flat key-value from decrypted events) */
    event: Record<string, unknown>;
    /** Resolved participant entities mapping lowercase email to entity info (email threads only) */
    participant_entities?: Record<
      string,
      { entity_id: string; entity_type_name: string; display_name: string }
    >;
  };
  /** URL to view in CRM (e.g., /{org_id}/crm/ahoy_meeting_notes_entity/{id}) */
  external_link: string;
  /** Associated calendar event (for meeting notes) */
  associated_meeting?: AssociatedMeeting | null;
}

/**
 * Email thread trigger data returned by ?include=trigger API
 * Shows the email thread that triggered a proposal
 */
/**
 * Union type for all trigger data variants
 * Discriminated by trigger_type field
 */
export type TriggerData = TriggerEmailData | CrmEventTriggerData;

/**
 * Non-trigger sentinel entry the endpoint returns in place of a trigger when
 * one can't be resolved (no thread_id, unresolved entity, etc.)
 */
export interface TriggerSentinel {
  error: string;
  code: string;
}

/**
 * API response envelope for triggers
 */
export interface TriggersResponse {
  triggers: Array<TriggerData | TriggerSentinel>;
}

/**
 * Type guard for CRM event trigger data
 */
export function isCrmEventTrigger(
  trigger: TriggerData | TriggerSentinel | null | undefined
): trigger is CrmEventTriggerData {
  return (
    (trigger as CrmEventTriggerData)?.trigger_type === 'crm_event' &&
    (trigger as CrmEventTriggerData)?.crm_event != null
  );
}

/**
 * Type guard for email trigger data
 */
export function isEmailTrigger(
  trigger: TriggerData | TriggerSentinel | null | undefined
): trigger is TriggerEmailData {
  return (
    (trigger as TriggerEmailData)?.trigger_type === 'email' &&
    (trigger as TriggerEmailData)?.email != null
  );
}

/**
 * CRM-event entity types elevated to "high priority" — surfaced as their own
 * prominent cards rather than bundled into a summary (e.g. Action Overview).
 *
 * Hardcoded for now. The intent is for the backend to drive this per proposal
 * once the pattern is solid; at that point this constant becomes the fallback
 * default and the BE-provided list takes precedence.
 */
export const HIGH_PRIORITY_TRIGGER_ENTITY_TYPES: readonly string[] = [
  'ahoy_email_thread',
];

const HIGH_PRIORITY_TRIGGER_ENTITY_TYPE_SET = new Set(
  HIGH_PRIORITY_TRIGGER_ENTITY_TYPES
);

/**
 * Whether a trigger is high priority — surfaced as its own standalone card
 * rather than bundled with other triggers. Plain email triggers (the email
 * being acted on) are always high priority; CRM-event triggers qualify when
 * their entity type is in {@link HIGH_PRIORITY_TRIGGER_ENTITY_TYPES}.
 */
export function isHighPriorityTrigger(
  trigger: TriggerData | null | undefined
): boolean {
  if (isEmailTrigger(trigger)) {
    return true;
  }
  return (
    isCrmEventTrigger(trigger) &&
    HIGH_PRIORITY_TRIGGER_ENTITY_TYPE_SET.has(
      trigger.crm_event.entity_type_name
    )
  );
}

// ============================================================================
// Bulk Operations Types
// ============================================================================

/**
 * Request for bulk rejecting proposals
 */
export interface BulkRejectRequest {
  /** Filter by tag (e.g., "email", "calendar") */
  filter_tags__contains?: FilterTag;
  /** Rejection reason */
  reason?: string;
}

/**
 * Response from bulk reject operation
 */
export interface BulkRejectResponse {
  /** Number of proposals successfully rejected */
  rejected_count: number;
  /** Number of proposals skipped (already resolved) */
  skipped_count: number;
  /** Number of proposals that failed to reject */
  failed_count: number;
}

/**
 * Request for bulk marking proposals as read
 */
export interface BulkMarkReadRequest {
  /** Filter by tag (e.g., "email", "calendar") */
  filter_tags__contains?: FilterTag;
}

/**
 * Response from bulk mark-read operation
 */
export interface BulkMarkReadResponse {
  /** Number of proposals successfully marked as read */
  marked_count: number;
}
